import {
  rosterKind,
  stepKind,
  type BondStep,
  type ChangeDeclaration,
} from "@/lib/brokerBonds";
import type { BondRenderField } from "@/lib/bondFields";

// BRK-S162 — a REPEAT request, for an insured who already holds a standing
// program.
//
// BRK-S134 established the first half: a granted, account-sourced preapproval
// means this order runs no checks, so the Checks and Indication chips are
// omitted. What it left in place is the part a broker notices — every fields
// step re-rendered with PMS's prefill, so a repeat bid bond asks them to walk
// past their own business details and their own indemnitor roster, one Continue
// at a time, to change nothing.
//
// The scoping document's answer (§ Repeat requests) is to show what Signal
// already holds and ask ONE question: has any of it changed? Only what they say
// changed opens for edit.
//
// ── WHICH STEP IS "BUSINESS" AND WHICH IS "INDEMNITOR" ──────────────────────
//
// Decided STRUCTURALLY, from what the configuration declares, and never from a
// step's key or title. A fields step that declares `roster: "indemnity"` is the
// indemnitor roster — that is a contract property (PMS-S341 / BRK-S152), and it
// is what the whole roster surface already keys on. Every OTHER fields step is
// business information about the insured.
//
// The alternative — matching `key === "indemnitor"` — is exactly the
// name-inference D-10 rule 1 forbids for controls, and for the same reason: the
// configuration is Greg's to rename, and a rule that reads a label would
// silently reclassify a step the day it was retitled. A configuration with no
// roster step therefore has no indemnitor half, and the question asks about the
// business alone rather than offering a choice that addresses nothing.
//
// ── WHAT A DECLARATION IS NOT ───────────────────────────────────────────────
//
// It is not a claim about the ACCOUNT. Saying "nothing changed" hides steps for
// THIS order; it does not assert anything about the insured's record, and it
// writes nothing to it. The values PMS prefilled are the values that file.

// The WIRE shape lives in `brokerBonds.ts` with the rest of the seam
// (`ChangeDeclaration`) and is re-exported here, so a consumer of this module
// imports one thing. What lives HERE is the four answers a broker picks from and
// which configuration steps each covers — product rules, not contract.
export type { ChangeDeclaration } from "@/lib/brokerBonds";

/** The four answers the question offers, as one value the radio group holds. */
export type ChangeChoice = "none" | "business" | "indemnitors" | "both";

/** The choices in the order they are asked, with the words a broker reads. */
export const CHANGE_CHOICES: readonly {
  value: ChangeChoice;
  label: string;
  /** Rendered under the label. Says what picking it DOES, because the
   *  consequence — which steps open — is the thing a broker is choosing. */
  hint: string;
}[] = [
  {
    value: "none",
    label: "No — everything above is still correct",
    hint: "We’ll use what’s on file and take you straight to the bond details.",
  },
  {
    value: "business",
    label: "The business details have changed",
    hint: "You’ll review and update the business information.",
  },
  {
    value: "indemnitors",
    label: "The indemnitor details have changed",
    hint: "You’ll review and update who indemnifies the bond.",
  },
  {
    value: "both",
    label: "Both have changed",
    hint: "You’ll review and update both.",
  },
];

/** The declaration a choice means. */
export function declarationFor(choice: ChangeChoice): ChangeDeclaration {
  return {
    business: choice === "business" || choice === "both",
    indemnitors: choice === "indemnitors" || choice === "both",
  };
}

/** The choice a stored declaration reads as — so a resumed order shows the
 *  answer it was given rather than an empty radio group. */
export function choiceFor(declaration: ChangeDeclaration): ChangeChoice {
  if (declaration.business && declaration.indemnitors) return "both";
  if (declaration.business) return "business";
  if (declaration.indemnitors) return "indemnitors";
  return "none";
}

/** Which half of the question a step belongs to, or `null` for a step the
 *  question does not cover (a data pull, the preapproval, an unsupported
 *  type — none of which is information the broker restates). */
export type RepeatStepClass = "business" | "indemnitors";

export function repeatStepClass(step: BondStep): RepeatStepClass | null {
  if (stepKind(step) !== "fields") return null;
  return rosterKind(step) === "indemnity" ? "indemnitors" : "business";
}

/** The fields steps a summary covers, in configuration order. */
export function summarisedSteps(steps: readonly BondStep[]): BondStep[] {
  return steps.filter((step) => repeatStepClass(step) !== null);
}

/**
 * Whether a step OPENS for edit under a declaration.
 *
 * `null` — the broker has not answered yet — closes every summarised step, so
 * the spine behind the summary is the shortest it can be and grows as they
 * answer. A step the question does not cover is untouched: a data pull is not
 * information anybody restates, and hiding one would skip work the
 * configuration declared.
 */
export function stepOpensForEdit(
  step: BondStep,
  declaration: ChangeDeclaration | null,
): boolean {
  const cls = repeatStepClass(step);
  if (cls === null) return true;
  if (declaration === null) return false;
  return declaration[cls];
}

/**
 * Does the question have an indemnitor half to ask about?
 *
 * False for a configuration that declares no roster step — and then the two
 * choices naming indemnitors are not offered, rather than offered and
 * addressing nothing.
 */
export function hasIndemnitorHalf(steps: readonly BondStep[]): boolean {
  return steps.some((step) => repeatStepClass(step) === "indemnitors");
}

/** The choices to offer for a configuration — the full four, or the two that
 *  mean anything when there is no roster step. */
export function choicesFor(steps: readonly BondStep[]): typeof CHANGE_CHOICES {
  return hasIndemnitorHalf(steps)
    ? CHANGE_CHOICES
    : CHANGE_CHOICES.filter(
        (c) => c.value === "none" || c.value === "business",
      );
}

/**
 * Fields a repeat request may not edit (BRK-S162).
 *
 * The EIN, and only the EIN. PMS makes a set `account.ein` immutable on every
 * write path (PMS-S462 — a 400 `EIN_IMMUTABLE`), so a control for it is an input
 * whose only possible use is a rejected save. Matched on the declared **format**
 * rather than on a field key, for the same reason `repeatStepClass` reads the
 * roster declaration: a configuration is Greg's to rename, and a rule that read
 * a key would stop firing the day the field was re-pathed.
 *
 * This applies ONLY to the repeat spine. On a first request the EIN is being
 * SET, which is the one write PMS still accepts.
 */
export function isImmutableOnRepeat(field: BondRenderField): boolean {
  return field.format === "ein";
}

/**
 * The editable subset of a step's render fields, for a repeat request.
 *
 * REMOVED rather than disabled: a greyed-out box invites a click that does
 * nothing, and the value has a better home — the summary shows it with the
 * reason it cannot change. The stored value still rides out on the step save
 * untouched, which is what keeps that save from looking like a change to PMS.
 */
export function withoutImmutableFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter((field) => !isImmutableOnRepeat(field));
}

/** The immutable fields, for the surface that DOES show them. */
export function immutableFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter(isImmutableOnRepeat);
}
